/**
 * Customer (prime) deadlines on the CMMC scorecard — H3-13 (PR #48 back end,
 * migration 314; owner decision 2026-10-05: in scope). PR A r2 panel fixes.
 *
 *   CustomerDeadlineForm   customer, what they need, by when. Opened from the
 *                          hero's quiet "+ Add a customer deadline" (below the
 *                          actions), as Edit in the list, and "Add another".
 *                          A past date warns and needs a second click; a
 *                          failed submit focuses the first invalid field;
 *                          Esc / Cancel close an edit.
 *   CustomerDeadlineList   lead / admin: the live requirements behind the
 *                          "Customer deadlines" disclosure — Edit (focus in;
 *                          Save / Cancel return focus to that Edit), Archive
 *                          (focus to the next row; the last one → the hero's "+ Add"). The
 *                          scorecard owns "Archived X · Undo" (Undo re-creates the row).
 *
 * Members never see these controls (read only: the deadline line). Writes:
 * POST / PUT …/customer-requirements[/:id], POST …/:id/archive — program lead
 * or owner/admin; the server enforces it (403) whatever the UI shows.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select } from '@aigovops/design-system';
import {
  archiveCustomerRequirement, createCustomerRequirement, updateCustomerRequirement,
  type CustomerRequirement, type CustomerRequirementInput,
} from '../../lib/api';
import { invalidationMap } from '../../lib/query-keys';
import { fmtShort } from '../../lib/cmmc-model';

type Status = CustomerRequirementInput['required_status'];
const STATUS_LABEL: Record<Status, string> = { met: 'Level 1 met', conditional: 'Level 2 conditional', final: 'Level 2 final' };

/** localStorage key for "No customer deadline" (per workspace × period × viewer; a convenience, not data). */
export const dismissedKey = (slug: string, periodId: string, viewerId: string) => `gp-cmmc-no-customer:${slug}:${periodId}:${viewerId}`;

function useRefresh(slug: string) {
  const qc = useQueryClient();
  return () => Promise.all([['customer-requirements', slug] as const, ...invalidationMap['program:changed'](slug)].map((queryKey) => qc.invalidateQueries({ queryKey })));
}

const todayDay = () => new Date().toISOString().slice(0, 10);

export function CustomerDeadlineForm({ slug, program, level, initial, onDone, onCancel, idPrefix = 'cust', submitLabel = 'Save deadline', autoFocus, extra, announce }: {
  slug: string; program: string; level: '1' | '2'; initial?: CustomerRequirement; onDone?: (saved: CustomerRequirementInput) => void; onCancel?: () => void;
  idPrefix?: string; submitLabel?: string; autoFocus?: boolean; extra?: ReactNode; announce?: (s: string) => void;
}) {
  const refresh = useRefresh(slug);
  const statuses: Status[] = level === '1' ? ['met'] : ['conditional', 'final'];
  const [name, setName] = useState(initial?.customer_name ?? '');
  const [status, setStatus] = useState<Status>((initial?.required_status as Status | undefined) ?? statuses[0]!);
  const [by, setBy] = useState(initial?.required_by ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pastOk, setPastOk] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const nameId = `${idPrefix}-name`, byId = `${idPrefix}-by`;
  useEffect(() => { if (autoFocus) document.getElementById(nameId)?.focus(); }, [autoFocus, nameId]);
  const past = !!by && by < todayDay();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name the customer.';
    if (!by) errs.by = 'Choose the date they need it by.';
    setErrors(errs);
    if (Object.keys(errs).length) { document.getElementById(errs.name ? nameId : byId)?.focus(); return; }
    // A past date is allowed (a missed deadline is still a record) but needs a second click.
    if (past && !pastOk) { setPastOk(true); return; }
    setSaving(true); setFormError(null); announce?.('Saving…');
    const body: CustomerRequirementInput = { customer_name: name.trim(), required_level: level, required_status: status, required_by: by };
    try {
      if (initial) await updateCustomerRequirement(slug, program, initial.customer_requirement_id, body);
      else await createCustomerRequirement(slug, program, body);
      await refresh();
      announce?.('Deadline saved');
      if (!initial) { setName(''); setBy(''); setPastOk(false); }
      onDone?.(body);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'The deadline could not be saved.');
      announce?.('');
    } finally { setSaving(false); }
  };
  return (
    <form ref={formRef} className="gp-cmmc-cust-form" onSubmit={submit} noValidate aria-label={initial ? `Edit ${initial.customer_name}` : 'Add a customer deadline'} data-part="cust-form"
      onKeyDown={(e) => { if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); } }}>
      {formError ? <Alert variant="error">{formError}</Alert> : null}
      <Field id={nameId} label="Customer" required error={errors.name}>
        <Input value={name} maxLength={200} onChange={(e) => setName(e.currentTarget.value)} />
      </Field>
      {statuses.length > 1 ? (
        <Field id={`${idPrefix}-status`} label="Needs">
          <Select value={status} onChange={(e) => setStatus(e.currentTarget.value as Status)}>
            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </Select>
        </Field>
      ) : <p className="gp-cmmc-static" data-part="needs">{`Needs ${STATUS_LABEL[statuses[0]!]}`}</p>}
      <Field id={byId} label="By" required error={errors.by}>
        <Input type="date" value={by} onChange={(e) => { setBy(e.currentTarget.value); setPastOk(false); }} />
      </Field>
      {past && pastOk ? <p className="gp-cmmc-warn" role="alert" data-part="past-warning">That date has passed · save as missed?</p> : null}
      <span className="gp-actions">
        <Button type="submit" variant="secondary" aria-disabled={saving || undefined}>{saving ? 'Saving…' : past && pastOk ? 'Save as missed' : submitLabel}</Button>
        {onCancel ? <Button variant="ghost" onClick={onCancel}>Cancel</Button> : null}
        {extra}
      </span>
    </form>
  );
}

export function CustomerDeadlineList({ slug, program, level, requirements, announce, onArchived, onAction }: {
  slug: string; program: string; level: '1' | '2'; requirements: readonly CustomerRequirement[]; announce?: (s: string) => void;
  /** The scorecard keeps the Undo (it outlives this list when the last row goes). */
  onArchived?: (r: CustomerRequirement, wasLast: boolean) => void;
  /** Any other action here clears a pending Undo. */
  onAction?: () => void;
}) {
  const refresh = useRefresh(slug);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const returnTo = useRef<string | null>(null);
  const live = requirements.filter((r) => !r.archived_at);
  useEffect(() => {
    if (!returnTo.current) return;
    const el = document.getElementById(returnTo.current);
    if (el) { el.focus(); returnTo.current = null; }
  });
  const editId = (r: CustomerRequirement) => `cust-edit-${r.customer_requirement_id}`;
  const closeEdit = (r: CustomerRequirement) => { returnTo.current = editId(r); setEditing(null); };
  const archive = async (r: CustomerRequirement) => {
    if (busy) return;
    setBusy(r.customer_requirement_id); setError(null);
    const i = live.indexOf(r);
    const next = live[i + 1] ?? live[i - 1];
    try {
      await archiveCustomerRequirement(slug, program, r.customer_requirement_id);
      // The next row's Edit; the last one goes → the scorecard focuses its re-rendered "+ Add".
      if (next) returnTo.current = editId(next);
      onArchived?.(r, !next);
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'The deadline could not be archived.'); }
    finally { setBusy(null); }
  };
  return (
    <details className="gp-cmmc-disclosure" data-part="cust-list">
      <summary id="cust-summary">{`Customer deadlines · ${live.length}`}</summary>
      <div className="gp-cmmc-disclosure-body">
        {error ? <Alert variant="error">{error}</Alert> : null}
        <ul className="gp-cmmc-rows" aria-label="Customer deadlines">
          {live.map((r) => (
            <li key={r.customer_requirement_id} data-requirement={r.customer_requirement_id}>
              {editing === r.customer_requirement_id ? (
                <CustomerDeadlineForm slug={slug} program={program} level={level} initial={r} idPrefix={`cust-${r.customer_requirement_id}`} submitLabel="Save" autoFocus announce={announce}
                  onDone={() => closeEdit(r)} onCancel={() => closeEdit(r)} />
              ) : <>
                <span className="gp-cmmc-name"><span>{r.customer_name}</span></span>
                <span className="gp-cmmc-meta">{`${STATUS_LABEL[r.required_status as Status] ?? r.required_status} by ${fmtShort(r.required_by)}`}</span>
                <Button compact id={editId(r)} onClick={() => { onAction?.(); setEditing(r.customer_requirement_id); }} aria-label={`Edit · ${r.customer_name}`}>Edit</Button>
                <Button compact aria-disabled={busy === r.customer_requirement_id || undefined} onClick={() => void archive(r)} aria-label={`Archive · ${r.customer_name}`}>
                  {busy === r.customer_requirement_id ? 'Archiving…' : 'Archive'}
                </Button>
              </>}
            </li>
          ))}
        </ul>
        <CustomerDeadlineForm slug={slug} program={program} level={level} idPrefix="cust-more" submitLabel="Add another" announce={announce} onDone={() => onAction?.()} />
      </div>
    </details>
  );
}
